import { useCallback, useState } from 'react';
import type { Workflow, WorkflowStage } from '@/components/ApprovalStageWizard/types';

export interface UseStageDraftParams {
  isLive: boolean;
  selectedWorkflowId: string | null;
  workflows: Workflow[];
  setWorkflows: React.Dispatch<React.SetStateAction<Workflow[]>>;
}

export interface UseStageDraftResult {
  newStageIdToEdit: string | null;
  newStageName: string | undefined;
  handleAddStage: (stageName?: string, placeholderStageId?: string) => void;
  handleClearInitialEditingStageId: () => void;
}

const isTempStageId = (stageId: string): boolean =>
  stageId.startsWith('temp-') || stageId.startsWith('placeholder-');

/**
 * Owns the transient "stage being created" state that drives the stage wizard,
 * including cleanup of unsaved temporary stages when the wizard is closed.
 */
export const useStageDraft = ({
  isLive,
  selectedWorkflowId,
  workflows,
  setWorkflows,
}: UseStageDraftParams): UseStageDraftResult => {
  const [newStageIdToEdit, setNewStageIdToEdit] = useState<string | null>(null);
  const [newStageName, setNewStageName] = useState<string | undefined>(undefined);

  const handleAddStage = useCallback((stageName?: string, placeholderStageId?: string) => {
    if (!selectedWorkflowId) return;

    const selectedWorkflow = workflows.find((w) => w.id === selectedWorkflowId);
    if (!selectedWorkflow) return;

    const newStageOrder = selectedWorkflow.stages.length + 1;
    // If creating from a placeholder, use the placeholder ID so the rule reference resolves
    const stageId = placeholderStageId || `temp-stage-${Date.now()}-${newStageOrder}`;
    const newStage: WorkflowStage = {
      id: stageId,
      order: newStageOrder,
      name: stageName || `Stage ${newStageOrder}`, // Use provided name or default
      description: '',
      status: 'draft',
    };

    setWorkflows((prev) =>
      prev.map((workflow) => {
        if (workflow.id !== selectedWorkflowId) return workflow;

        return {
          ...workflow,
          stages: [...workflow.stages, newStage],
          updatedAt: new Date(),
        };
      })
    );

    // Open the configuration wizard immediately with pre-filled name
    setNewStageIdToEdit(stageId);
    setNewStageName(stageName); // Pass the stage name to pre-fill in wizard
  }, [selectedWorkflowId, workflows, setWorkflows]);

  const handleClearInitialEditingStageId = useCallback(() => {
    // In live mode, remove any unsaved temp stages when wizard is closed
    // This prevents UI drift from server state
    if (isLive && newStageIdToEdit && isTempStageId(newStageIdToEdit)) {
      setWorkflows((prev) =>
        prev.map((workflow) => {
          if (workflow.id !== selectedWorkflowId) return workflow;
          // Check if the temp stage was saved (has stageData configured)
          const tempStage = workflow.stages.find((s) => s.id === newStageIdToEdit);
          if (tempStage && !tempStage.stageData) {
            // Stage was never configured, remove it
            return {
              ...workflow,
              stages: workflow.stages.filter((s) => s.id !== newStageIdToEdit),
            };
          }
          return workflow;
        })
      );
    }
    setNewStageIdToEdit(null);
    setNewStageName(undefined); // Clear stage name when closing wizard
  }, [isLive, newStageIdToEdit, selectedWorkflowId, setWorkflows]);

  return { newStageIdToEdit, newStageName, handleAddStage, handleClearInitialEditingStageId };
};
